/**
 * Use-case specific genogram templates with comprehensive field data
 */

import { CreateNodeCommand } from '../../../core/commands/CreateNodeCommand';
import { CreateEdgeCommand } from '../../../core/commands/CreateEdgeCommand';
import { CompositeCommand } from '../../../core/commands/ICommand';
import type { Node } from '../../../core/models/scene';
import { getShapeManifest } from '../../../lib/diagram/registries';

const PERSON_SIZE = Math.max(
  1,
  Number(getShapeManifest('genogram', 'person')?.defaultDimensions?.width) || 60
);
const HORIZONTAL_SPACING = Math.max(260, PERSON_SIZE * 4);
const VERTICAL_SPACING = Math.max(200, PERSON_SIZE * 3);
const COUPLE_HORIZONTAL_OFFSET = Math.max(180, HORIZONTAL_SPACING * 0.65);

let idCounter = 0;
const getUniqueId = () => `person-${Date.now()}-${idCounter++}-${Math.random().toString(36).substr(2, 9)}`;

interface PersonData {
  // Core Identity (base.pack.ts)
  name: string;
  sex: 'male' | 'female' | 'unknown';
  age?: number;
  birthYear?: number;
  deathYear?: number;
  deceased?: boolean;
  isIndexPerson?: boolean;
  culturalHeritageTags?: string[];
  religion?: string;
  location?: string;
  contactInfo?: string;
  occupation?: string;
  education?: string;

  // Medical & Health (medical-genetic.pack.ts)
  medicalConditionTags?: string[];
  medications?: string[];  // Field ID: medications
  allergies?: string[];    // MULTI_SELECT
  surgeries?: string[];    // MULTI_SELECT
  medicalNotes?: string;
  causeOfDeath?: string;

  // Mental Health & Genetic
  substanceUse?: string[]; // MULTI_SELECT
  traumaHistory?: string[];// MULTI_SELECT
  mentalHealthTreatment?: string[];
  therapyNotes?: string;
  geneticMarkers?: string[]; // Field ID: geneticMarkers
  intergenerationalPatterns?: string;
  screening?: string;
  immunizations?: string;
  riskFactors?: string[];

  // Legal & Estate (legal-estate.pack.ts)
  legalStatus?: string;
  guardianship?: string;
  willStatus?: string;
  legalNotes?: string;
  executor?: string;
  trustee?: string;
  powerOfAttorney?: string;
  advanceDirectives?: string;
  beneficiaries?: string;

  // Financial (legal-estate.pack.ts, social-work.pack.ts)
  income?: string;  // Field ID: income
  employmentStatus?: string;
  assets?: string;
  debts?: string;
  insurance?: string;

  // Social & Professional (social-work.pack.ts)
  housingStatus?: string;
  educationLevel?: string;
  servicesReceived?: string[];
  socialNotes?: string;
  strengths?: string[];
  supportNetwork?: string; // Field ID: supportNetwork

  // Relationships (family-therapy.pack.ts)
  maritalStatus?: string;
  emotionalBonds?: string;
  attachmentStyle?: string;
  communicationStyle?: string;
  copingStrategies?: string[];
  familyRole?: string;
  treatmentGoals?: string;

  // Research (general-research.pack.ts)
  researchId?: string;
  cohort?: string; // Field ID: cohort (not studyGroup)
  consentStatus?: string; // Field ID: consentStatus
  biospecimen?: string;
  collectionDate?: string;
  dataQuality?: string;
  dataSources?: string[];
  researchNotes?: string;
  publicationStatus?: string;
  followUpStatus?: string;
  confidentialityLevel?: string;

  // Notes
  notes?: string;
}

function createPerson(data: PersonData, x: number, y: number): { node: Node; id: string } {
  const id = getUniqueId();
  const node: Node = {
    id,
    type: 'person',
    category: 'genogram',
    x,
    y,
    w: PERSON_SIZE,
    h: PERSON_SIZE,
    width: PERSON_SIZE,
    height: PERSON_SIZE,
    name: data.name,
    sex: data.sex,
    age: data.age,
    birthYear: data.birthYear,
    deathYear: data.deathYear,
    deceased: data.deceased,
    indexPerson: data.isIndexPerson || false,
    culturalHeritageTags: data.culturalHeritageTags,
    religion: data.religion,
    location: data.location,
    contactInfo: data.contactInfo,
    medicalConditionTags: data.medicalConditionTags,
    medications: data.medications,
    allergies: data.allergies,
    surgeries: data.surgeries,
    medicalNotes: data.medicalNotes,
    causeOfDeath: data.causeOfDeath,
    substanceUse: data.substanceUse,
    traumaHistory: data.traumaHistory,
    mentalHealthTreatment: data.mentalHealthTreatment,
    therapyNotes: data.therapyNotes,
    geneticMarkers: data.geneticMarkers,
    intergenerationalPatterns: data.intergenerationalPatterns,
    screening: data.screening,
    immunizations: data.immunizations,
    riskFactors: data.riskFactors,
    legalStatus: data.legalStatus,
    guardianship: data.guardianship,
    willStatus: data.willStatus,
    legalNotes: data.legalNotes,
    executor: data.executor,
    trustee: data.trustee,
    powerOfAttorney: data.powerOfAttorney,
    advanceDirectives: data.advanceDirectives,
    beneficiaries: data.beneficiaries,
    income: data.income,
    employmentStatus: data.employmentStatus,
    assets: data.assets,
    debts: data.debts,
    insurance: data.insurance,
    housingStatus: data.housingStatus,
    educationLevel: data.educationLevel,
    servicesReceived: data.servicesReceived,
    socialNotes: data.socialNotes,
    strengths: data.strengths,
    supportNetwork: data.supportNetwork,
    occupation: data.occupation,
    education: data.education,
    maritalStatus: data.maritalStatus,
    emotionalBonds: data.emotionalBonds,
    attachmentStyle: data.attachmentStyle,
    communicationStyle: data.communicationStyle,
    copingStrategies: data.copingStrategies,
    familyRole: data.familyRole,
    treatmentGoals: data.treatmentGoals,
    researchId: data.researchId,
    cohort: data.cohort,
    consentStatus: data.consentStatus,
    biospecimen: data.biospecimen,
    collectionDate: data.collectionDate,
    dataQuality: data.dataQuality,
    dataSources: data.dataSources,
    researchNotes: data.researchNotes,
    publicationStatus: data.publicationStatus,
    followUpStatus: data.followUpStatus,
    confidentialityLevel: data.confidentialityLevel,
    notes: data.notes,
    attrs: {
      ...(data.isIndexPerson ? { indexPerson: true, isIndexPerson: true } : {}),
      exampleData: true,
    },
  } as Node;

  console.log('[Template] Created person node:', {
    id,
    name: node.name,
    medications: (node as any).medications,
    geneticMarkers: (node as any).geneticMarkers,
    allergies: (node as any).allergies,
    allKeys: Object.keys(node)
  });

  return { node, id };
}

function createMarriage(id1: string, id2: string): CreateEdgeCommand {
  return new CreateEdgeCommand({
    id: `marriage-${id1}-${id2}`,
    type: 'marriage',
    from: id1,
    to: id2,
    category: 'genogram',
    fromAnchor: 'right',
    toAnchor: 'left',
  });
}

function createChild(parentId: string, childId: string, idx: number = 0): CreateEdgeCommand {
  return new CreateEdgeCommand({
    id: `child-${parentId}-${childId}-${idx}`,
    type: 'child',
    from: parentId,
    to: childId,
    category: 'genogram',
    fromAnchor: 'bottom',
    toAnchor: 'top',
  });
}

/**
 * MEDICAL/GENETIC USE CASE - Focus on hereditary conditions, medical history
 */
export function createMedicalGeneticTemplate(startX: number, startY: number): CompositeCommand {
  const commands: (CreateNodeCommand | CreateEdgeCommand)[] = [];

  // Grandfather (deceased, heart disease)
  const grandfather = createPerson({
    name: 'Thomas Anderson',
    sex: 'male',
    age: 82,
    birthYear: 1942,
    deathYear: 2020,
    deceased: true,
    culturalHeritageTags: ['caucasian', 'scandinavian'],
    location: 'Minneapolis, MN',
    medicalConditionTags: ['heart_disease', 'stroke'],
    geneticMarkers: ['familial_hypercholesterolemia'],
    medications: [],
    causeOfDeath: 'Myocardial infarction',
    intergenerationalPatterns: 'Cardiovascular disease runs in paternal line',
    notes: 'First heart attack at age 58, second fatal at 78',
  }, startX - HORIZONTAL_SPACING, startY - VERTICAL_SPACING * 2);

  // Grandmother (hypertension, diabetes)
  const grandmother = createPerson({
    name: 'Helen Anderson',
    sex: 'female',
    age: 79,
    birthYear: 1945,
    culturalHeritageTags: ['caucasian', 'scandinavian'],
    location: 'Minneapolis, MN',
    medicalConditionTags: ['hypertension', 'type_2_diabetes', 'osteoporosis'],
    geneticMarkers: ['brca2_mutation'],
    medications: ['Lisinopril 10mg daily', 'Metformin 1000mg twice daily', 'Calcium + Vit D'],
    allergies: ['Penicillin (anaphylaxis)'],
    surgeries: ['Bilateral mastectomy (2010)'],
    intergenerationalPatterns: 'Diabetes and breast cancer in maternal grandmother',
    notes: 'Regular monitoring for cancer recurrence. Well-controlled diabetes.',
  }, startX + HORIZONTAL_SPACING, startY - VERTICAL_SPACING * 2);

  // Father (index person - has genetic condition)
  const father = createPerson({
    name: 'Michael Anderson',
    sex: 'male',
    age: 52,
    birthYear: 1972,
    isIndexPerson: true,
    culturalHeritageTags: ['caucasian', 'scandinavian'],
    location: 'St. Paul, MN',
    contactInfo: 'michael.anderson@email.com',
    maritalStatus: 'married',
    medicalConditionTags: ['familial_hypercholesterolemia', 'hypertension'],
    geneticMarkers: ['brca2_mutation', 'familial_hypercholesterolemia'],
    medications: ['Atorvastatin 80mg daily', 'Amlodipine 5mg daily', 'Aspirin 81mg daily'],
    allergies: ['None known'],
    surgeries: ['Appendectomy (1995)'],
    occupation: 'Cardiologist',
    education: 'doctorate',
    employmentStatus: 'Full-time',
    intergenerationalPatterns: 'Father died of MI, mother has BRCA2, both have cholesterol issues',
    notes: 'Genetic testing confirmed BRCA2 and FH. On aggressive statin therapy. Annual cardiac stress tests.',
  }, startX - COUPLE_HORIZONTAL_OFFSET, startY - VERTICAL_SPACING);

  // Mother
  const mother = createPerson({
    name: 'Jennifer Lee',
    sex: 'female',
    age: 50,
    birthYear: 1974,
    culturalHeritageTags: ['asian', 'korean'],
    location: 'St. Paul, MN',
    contactInfo: 'jennifer.anderson@email.com',
    maritalStatus: 'married',
    medicalConditionTags: ['asthma', 'seasonal_allergies'],
    geneticMarkers: [],
    medications: ['Albuterol inhaler PRN', 'Fluticasone nasal spray'],
    allergies: ['Shellfish (hives)'],
    surgeries: ['C-section x2 (2005, 2007)'],
    occupation: 'Pediatric Nurse Practitioner',
    education: 'masters',
    employmentStatus: 'Full-time',
    intergenerationalPatterns: 'No significant hereditary conditions',
    notes: 'Well-controlled asthma. Family history negative for major conditions.',
  }, startX + COUPLE_HORIZONTAL_OFFSET, startY - VERTICAL_SPACING);

  // Child 1 (tested positive for BRCA2)
  const child1 = createPerson({
    name: 'Sarah Anderson',
    sex: 'female',
    age: 19,
    birthYear: 2005,
    culturalHeritageTags: ['mixed', 'scandinavian', 'korean'],
    location: 'Boston, MA',
    contactInfo: 'sarah.anderson@college.edu',
    maritalStatus: 'single',
    medicalConditionTags: [],
    geneticMarkers: ['brca2_mutation'],
    medications: ['Oral contraceptives'],
    allergies: ['None known'],
    surgeries: [],
    occupation: 'College Student - Pre-Med',
    education: 'some_college',
    employmentStatus: 'Student',
    intergenerationalPatterns: 'Inherited BRCA2 from father. Increased screening protocol.',
    notes: 'Underwent genetic testing at 18. Enrolled in high-risk surveillance program.',
  }, startX - HORIZONTAL_SPACING, startY);

  // Child 2 (tested negative)
  const child2 = createPerson({
    name: 'Alex Anderson',
    sex: 'male',
    age: 17,
    birthYear: 2007,
    culturalHeritageTags: ['mixed', 'scandinavian', 'korean'],
    location: 'St. Paul, MN',
    contactInfo: 'alex.anderson@email.com',
    maritalStatus: 'single',
    medicalConditionTags: ['asthma'],
    geneticMarkers: [],
    medications: ['Albuterol inhaler PRN'],
    allergies: ['Pollen'],
    surgeries: [],
    occupation: 'High School Senior',
    education: 'high_school',
    employmentStatus: 'Student',
    intergenerationalPatterns: 'Negative for familial mutations. Inherited asthma from mother.',
    notes: 'Genetic testing at 16 showed no BRCA2 or FH mutations. Mild asthma.',
  }, startX + HORIZONTAL_SPACING, startY);

  commands.push(new CreateNodeCommand(grandfather.node));
  commands.push(new CreateNodeCommand(grandmother.node));
  commands.push(new CreateNodeCommand(father.node));
  commands.push(new CreateNodeCommand(mother.node));
  commands.push(new CreateNodeCommand(child1.node));
  commands.push(new CreateNodeCommand(child2.node));

  commands.push(createMarriage(grandfather.id, grandmother.id));
  commands.push(createMarriage(father.id, mother.id));
  commands.push(createChild(grandfather.id, father.id, 0));
  commands.push(createChild(grandmother.id, father.id, 1));
  commands.push(createChild(father.id, child1.id, 2));
  commands.push(createChild(mother.id, child1.id, 3));
  commands.push(createChild(father.id, child2.id, 4));
  commands.push(createChild(mother.id, child2.id, 5));

  return new CompositeCommand('Create Medical/Genetic Template', commands);
}

/**
 * MEDICAL HISTORY MAP - 3 generations with cardiac and metabolic risk patterns
 */
export function createMedicalHistoryMapTemplate(startX: number, startY: number): CompositeCommand {
  const commands: (CreateNodeCommand | CreateEdgeCommand)[] = [];

  // Generation 1: Grandparents
  const edward = createPerson({
    name: 'Edward Holloway',
    sex: 'male',
    age: 60,
    deceased: true,
    causeOfDeath: 'Heart attack',
    medicalConditionTags: ['heart_disease', 'hypertension'],
    medicalNotes: 'Deceased at 60 from myocardial infarction; early cardiac event on paternal line.'
  }, startX - HORIZONTAL_SPACING * 0.9, startY - VERTICAL_SPACING * 2);

  const martha = createPerson({
    name: 'Martha Holloway',
    sex: 'female',
    age: 84,
    medicalConditionTags: ['hypertension'],
    medicalNotes: 'Chronic hypertension; paternal matriarch still living.'
  }, startX - HORIZONTAL_SPACING * 0.3, startY - VERTICAL_SPACING * 2);

  const victor = createPerson({
    name: 'Victor Vance',
    sex: 'male',
    age: 67,
    deceased: true,
    medicalConditionTags: ['type_2_diabetes', 'kidney_failure'],
    causeOfDeath: 'Kidney failure complications',
    medicalNotes: 'Longstanding Type 2 diabetes leading to kidney failure; deceased at 67.'
  }, startX + HORIZONTAL_SPACING * 0.3, startY - VERTICAL_SPACING * 2);

  const elena = createPerson({
    name: 'Elena Vance',
    sex: 'female',
    age: 81,
    medicalConditionTags: ['osteoporosis'],
    medicalNotes: 'Living with osteoporosis; maternal matriarch.'
  }, startX + HORIZONTAL_SPACING * 0.9, startY - VERTICAL_SPACING * 2);

  // Generation 2: Parents & maternal uncle
  const julian = createPerson({
    name: 'Julian Holloway',
    sex: 'male',
    age: 55,
    medicalConditionTags: ['hyperlipidemia', 'hypertension'],
    medicalNotes: 'High cholesterol and hypertension; echoes paternal cardiac risk.'
  }, startX - COUPLE_HORIZONTAL_OFFSET, startY - VERTICAL_SPACING);

  const sarah = createPerson({
    name: 'Sarah Holloway',
    sex: 'female',
    age: 53,
    medicalConditionTags: ['gestational_diabetes_history', 'pre_diabetes'],
    medicalNotes: 'History of gestational diabetes; currently pre-diabetic with insulin resistance.'
  }, startX + COUPLE_HORIZONTAL_OFFSET, startY - VERTICAL_SPACING);

  const simon = createPerson({
    name: 'Simon Vance',
    sex: 'male',
    age: 49,
    medicalConditionTags: ['type_2_diabetes'],
    medicalNotes: 'Maternal uncle with Type 2 diabetes; reinforces maternal metabolic risk.'
  }, startX + HORIZONTAL_SPACING * 1.1, startY - VERTICAL_SPACING * 0.6);

  // Generation 3: Proband and partner
  const marcus = createPerson({
    name: 'Marcus Holloway',
    sex: 'male',
    age: 26,
    isIndexPerson: true,
    medicalNotes: 'Currently healthy. Clinical risk: high due to paternal early cardiac events and maternal insulin resistance.',
    riskFactors: ['family_history_heart_disease', 'family_history_diabetes']
  }, startX, startY);

  const chloe = createPerson({
    name: 'Chloe Bennett',
    sex: 'female',
    age: 25,
    medicalNotes: 'Healthy. Family history of breast cancer on maternal line (mother and grandmother) → consider BRCA screening for future children.',
    riskFactors: ['family_history_breast_cancer']
  }, startX + COUPLE_HORIZONTAL_OFFSET, startY);

  commands.push(
    new CreateNodeCommand(edward.node),
    new CreateNodeCommand(martha.node),
    new CreateNodeCommand(victor.node),
    new CreateNodeCommand(elena.node),
    new CreateNodeCommand(julian.node),
    new CreateNodeCommand(sarah.node),
    new CreateNodeCommand(simon.node),
    new CreateNodeCommand(marcus.node),
    new CreateNodeCommand(chloe.node)
  );

  // Marriages/partnerships
  commands.push(
    createMarriage(edward.id, martha.id),
    createMarriage(victor.id, elena.id),
    createMarriage(julian.id, sarah.id),
    createMarriage(marcus.id, chloe.id)
  );

  // Children links
  commands.push(
    createChild(edward.id, julian.id, 0),
    createChild(martha.id, julian.id, 1),
    createChild(victor.id, sarah.id, 2),
    createChild(elena.id, sarah.id, 3),
    createChild(victor.id, simon.id, 4),
    createChild(elena.id, simon.id, 5),
    createChild(julian.id, marcus.id, 6),
    createChild(sarah.id, marcus.id, 7)
  );

  return new CompositeCommand('Create Medical History Map Template', commands);
}

/**
 * MEDICAL/GENETIC – Hemophilia inheritance (Genetic Pedigree card)
 */
export function createGeneticDisorderMapTemplate(startX: number, startY: number): CompositeCommand {
  const commands: (CreateNodeCommand | CreateEdgeCommand)[] = [];

  // Generation 1: Grandparents
  const oliver = createPerson({
    name: 'Oliver Sterling',
    sex: 'male',
    medicalNotes: 'Healthy; no history of bleeding disorders.'
  }, startX - HORIZONTAL_SPACING, startY - VERTICAL_SPACING * 2);

  const alice = createPerson({
    name: 'Alice Sterling',
    sex: 'female',
    medicalNotes: 'Healthy.'
  }, startX - HORIZONTAL_SPACING * 0.5, startY - VERTICAL_SPACING * 2);

  const george = createPerson({
    name: 'George Thorne',
    sex: 'male',
    medicalConditionTags: ['hemophilia_a'],
    medicalNotes: 'Affected with Hemophilia A.'
  }, startX + HORIZONTAL_SPACING * 0.5, startY - VERTICAL_SPACING * 2);

  const beatrice = createPerson({
    name: 'Beatrice Thorne',
    sex: 'female',
    medicalNotes: 'Healthy; not a carrier.'
  }, startX + HORIZONTAL_SPACING, startY - VERTICAL_SPACING * 2);

  // Generation 2: Parents and maternal uncle
  const lawrence = createPerson({
    name: 'Lawrence Sterling',
    sex: 'male',
    medicalNotes: 'Healthy; inherited a healthy X from Alice and Y from Oliver.'
  }, startX - COUPLE_HORIZONTAL_OFFSET, startY - VERTICAL_SPACING);

  const isla = createPerson({
    name: 'Isla Sterling',
    sex: 'female',
    medicalNotes: 'Obligate carrier of Hemophilia A inherited from her father George; asymptomatic.',
    geneticMarkers: ['hemophilia_a_carrier'],
    medicalConditionTags: ['hemophilia_a_carrier']
  }, startX + COUPLE_HORIZONTAL_OFFSET, startY - VERTICAL_SPACING);

  // Generation 3: Proband, sibling, partner
  const leo = createPerson({
    name: 'Leo Sterling',
    sex: 'male',
    age: 24,
    isIndexPerson: true,
    medicalConditionTags: ['hemophilia_a'],
    medicalNotes: 'Proband; affected with Hemophilia A inherited from carrier mother Isla.'
  }, startX - COUPLE_HORIZONTAL_OFFSET * 0.4, startY);

  const elena = createPerson({
    name: 'Elena Rossi',
    sex: 'female',
    medicalNotes: 'Healthy partner with no family history of bleeding disorders.'
  }, startX + COUPLE_HORIZONTAL_OFFSET * 0.9, startY);

  // Generation 4: Children of Leo and Elena
  const lily = createPerson({
    name: 'Lily Sterling',
    sex: 'female',
    medicalNotes: 'Carrier; inherited X-linked Hemophilia A from father Leo.',
    geneticMarkers: ['hemophilia_a_carrier'],
    medicalConditionTags: ['hemophilia_a_carrier']
  }, startX - HORIZONTAL_SPACING * 0.6, startY + VERTICAL_SPACING);

  const nora = createPerson({
    name: 'Nora Sterling',
    sex: 'female',
    medicalNotes: 'Carrier; inherited X-linked Hemophilia A from father Leo.',
    geneticMarkers: ['hemophilia_a_carrier'],
    medicalConditionTags: ['hemophilia_a_carrier']
  }, startX, startY + VERTICAL_SPACING);

  const nathan = createPerson({
    name: 'Nathan Sterling',
    sex: 'male',
    medicalNotes: 'Healthy son; not a carrier and no hemophilia.',
  }, startX + HORIZONTAL_SPACING * 0.6, startY + VERTICAL_SPACING);

  commands.push(
    new CreateNodeCommand(oliver.node),
    new CreateNodeCommand(alice.node),
    new CreateNodeCommand(george.node),
    new CreateNodeCommand(beatrice.node),
    new CreateNodeCommand(lawrence.node),
    new CreateNodeCommand(isla.node),
    new CreateNodeCommand(leo.node),
    new CreateNodeCommand(elena.node),
    new CreateNodeCommand(lily.node),
    new CreateNodeCommand(nora.node),
    new CreateNodeCommand(nathan.node)
  );

  commands.push(
    createMarriage(oliver.id, alice.id),
    createMarriage(george.id, beatrice.id),
    createMarriage(lawrence.id, isla.id),
    createMarriage(leo.id, elena.id)
  );

  commands.push(
    createChild(oliver.id, lawrence.id, 0),
    createChild(alice.id, lawrence.id, 1),
    createChild(george.id, isla.id, 2),
    createChild(beatrice.id, isla.id, 3),
    createChild(lawrence.id, leo.id, 4),
    createChild(isla.id, leo.id, 5),
    createChild(leo.id, lily.id, 6),
    createChild(elena.id, lily.id, 7),
    createChild(leo.id, nora.id, 8),
    createChild(elena.id, nora.id, 9),
    createChild(leo.id, nathan.id, 10),
    createChild(elena.id, nathan.id, 11)
  );

  return new CompositeCommand('Create Genetic Disorder Map Template', commands);
}

/**
 * PSYCHOSOCIAL MAP – Multi-generation anxiety/enmeshment scenario (Miller-Chen)
 */
export function createPsychosocialMapTemplate(startX: number, startY: number): CompositeCommand {
  const commands: (CreateNodeCommand | CreateEdgeCommand)[] = [];

  // Generation 1: Great-grandparents (roots of pressure/distance)
  const arthur = createPerson({
    name: 'Arthur Miller',
    sex: 'male',
    deceased: true,
    occupation: 'Military Veteran',
    therapyNotes: 'Rigid, authoritative; emotionally distant from children.',
    traumaHistory: ['authoritarian_parenting'],
  }, startX - HORIZONTAL_SPACING * 0.6, startY - VERTICAL_SPACING * 2);

  const martha = createPerson({
    name: 'Martha Miller',
    sex: 'female',
    deceased: true,
    medicalConditionTags: ['depression'],
    therapyNotes: 'Diagnosed with “nervous exhaustion”; primary caregiver who stayed submissive to Arthur.',
  }, startX + HORIZONTAL_SPACING * 0.6, startY - VERTICAL_SPACING * 2);

  // Generation 2: Grandparents
  const robert = createPerson({
    name: 'Robert Miller',
    sex: 'male',
    age: 75,
    occupation: 'Surgeon',
    substanceUse: ['alcohol_use_disorder_in_remission'],
    therapyNotes: 'High-achieving; alcohol use disorder in remission; conflicted with sister Sarah.',
    emotionalBonds: 'conflict_with_sibling',
  }, startX - COUPLE_HORIZONTAL_OFFSET, startY - VERTICAL_SPACING);

  const susan = createPerson({
    name: 'Susan Miller',
    sex: 'female',
    age: 73,
    therapyNotes: 'Family gatekeeper; smooths over outbursts; enmeshed with daughter Elena.',
    emotionalBonds: 'enmeshed_with_daughter',
  }, startX + COUPLE_HORIZONTAL_OFFSET, startY - VERTICAL_SPACING);

  const sarah = createPerson({
    name: 'Sarah Miller',
    sex: 'female',
    age: 72,
    therapyNotes: 'Robert’s sister; hostile/conflicted relationship with him.',
    emotionalBonds: 'conflict_with_brother',
  }, startX + HORIZONTAL_SPACING * 1.1, startY - VERTICAL_SPACING * 0.7);

  // Generation 3: Parents
  const elena = createPerson({
    name: 'Elena Chen (Miller)',
    sex: 'female',
    age: 48,
    occupation: 'Lawyer',
    therapyNotes: 'Perfectionist; chronically stressed; prioritizes mother’s needs due to enmeshment.',
    traumaHistory: ['parentification'],
  }, startX - COUPLE_HORIZONTAL_OFFSET * 0.8, startY);

  const david = createPerson({
    name: 'David Chen',
    sex: 'male',
    age: 50,
    therapyNotes: 'Emotionally cutoff from his own family abroad; stays neutral to avoid triggering Elena’s anxiety.',
    familyRole: 'peacekeeper',
  }, startX + COUPLE_HORIZONTAL_OFFSET * 0.8, startY);

  // Generation 4: Children
  const leo = createPerson({
    name: 'Leo Chen',
    sex: 'male',
    age: 24,
    isIndexPerson: true,
    therapyNotes: 'Index person; severe anxiety and career paralysis; carries multi-generation achievement pressure; parentified by Elena.',
    mentalHealthTreatment: ['anxiety_counseling'],
  }, startX - HORIZONTAL_SPACING * 0.3, startY + VERTICAL_SPACING);

  const maya = createPerson({
    name: 'Maya Chen',
    sex: 'female',
    age: 20,
    therapyNotes: 'Identified dysfunction and moved away; maintains emotional distance for self-protection.',
    emotionalBonds: 'emotional_distance',
  }, startX + HORIZONTAL_SPACING * 0.3, startY + VERTICAL_SPACING);

  // Add nodes
  commands.push(
    new CreateNodeCommand(arthur.node),
    new CreateNodeCommand(martha.node),
    new CreateNodeCommand(robert.node),
    new CreateNodeCommand(susan.node),
    new CreateNodeCommand(sarah.node),
    new CreateNodeCommand(elena.node),
    new CreateNodeCommand(david.node),
    new CreateNodeCommand(leo.node),
    new CreateNodeCommand(maya.node)
  );

  // Marriages/partnerships
  commands.push(
    createMarriage(arthur.id, martha.id),
    createMarriage(robert.id, susan.id),
    createMarriage(elena.id, david.id)
  );

  // Emotional distance between Arthur and Martha (roots of repression)
  commands.push(
    new CreateEdgeCommand({
      type: 'distant',
      category: 'genogram',
      from: arthur.id,
      to: martha.id
    })
  );

  // Cutoff/estranged between Arthur and his son Robert
  commands.push(
    new CreateEdgeCommand({
      type: 'cutoff',
      category: 'genogram',
      from: arthur.id,
      to: robert.id,
      fromAnchor: 'left',
      toAnchor: 'left',
      fromOffset: 0.7,
      toOffset: 0.7,
    })
  );

  // Children links
  commands.push(
    new CreateEdgeCommand({
      id: `child-${arthur.id}-${robert.id}-0`,
      type: 'child',
      category: 'genogram',
      from: arthur.id,
      to: robert.id,
      fromAnchor: 'bottom',
      toAnchor: 'top',
      fromOffset: 0.3,
      toOffset: 0.35,
    }),
    new CreateEdgeCommand({
      id: `child-${martha.id}-${robert.id}-1`,
      type: 'child',
      category: 'genogram',
      from: martha.id,
      to: robert.id,
      fromAnchor: 'bottom',
      toAnchor: 'top',
      fromOffset: 0.7,
      toOffset: 0.65,
    }),
    createChild(arthur.id, sarah.id, 2),
    createChild(martha.id, sarah.id, 3),
    createChild(robert.id, elena.id, 4),
    createChild(susan.id, elena.id, 5),
    createChild(elena.id, leo.id, 6),
    createChild(david.id, leo.id, 7),
    createChild(elena.id, maya.id, 8),
    createChild(david.id, maya.id, 9)
  );

  return new CompositeCommand('Create Psychosocial Map Template', commands);
}

/**
 * FAMILY THERAPY USE CASE - Focus on relationships, emotional bonds, mental health
 */
export function createFamilyTherapyTemplate(startX: number, startY: number): CompositeCommand {
  const commands: (CreateNodeCommand | CreateEdgeCommand)[] = [];

  // Father
  const father = createPerson({
    name: 'Robert Martinez',
    sex: 'male',
    age: 45,
    birthYear: 1979,
    culturalHeritageTags: ['hispanic_latino', 'mexican'],
    location: 'Phoenix, AZ',
    contactInfo: '(602) 555-0123',
    maritalStatus: 'married',
    occupation: 'Sales Manager',
    education: 'bachelors',
    employmentStatus: 'Full-time',
    substanceUse: ['alcohol_recovering'],
    traumaHistory: ['witnessed_domestic_violence_as_child'],
    mentalHealthTreatment: ['Individual therapy (2019-present)', 'AA meetings'],
    therapyNotes: 'In recovery. Working on being more emotionally available.',
    medications: ['Sertraline 100mg daily'],
    emotionalBonds: 'Distant with children, working to reconnect',
    communicationStyle: 'Avoidance, withdraws during conflict',
    supportNetwork: 'AA sponsor, therapist, work colleagues',
    notes: 'In recovery. Working on being more emotionally available. Struggling with guilt.',
  }, startX - COUPLE_HORIZONTAL_OFFSET, startY);

  // Mother (Index Person)
  const mother = createPerson({
    name: 'Lisa Martinez',
    sex: 'female',
    age: 42,
    birthYear: 1982,
    isIndexPerson: true,
    culturalHeritageTags: ['caucasian', 'american'],
    location: 'Phoenix, AZ',
    contactInfo: '(602) 555-0124',
    maritalStatus: 'married',
    occupation: 'Elementary School Teacher',
    education: 'masters',
    employmentStatus: 'Full-time',
    substanceUse: ['none'],
    traumaHistory: ['emotional_neglect_in_childhood'],
    mentalHealthTreatment: ['Individual therapy (2020-present)', 'Couples therapy'],
    therapyNotes: 'Primary caregiver. Carries family emotional burden. Learning boundaries.',
    medications: ['Escitalopram 10mg daily'],
    emotionalBonds: 'Enmeshed with daughter, distant from son',
    communicationStyle: 'People-pleasing, over-functioning',
    copingStrategies: ['therapy', 'journaling'],
    supportNetwork: 'Therapist, sister, teacher colleagues',
    notes: 'Primary caregiver. Carries family emotional burden. Learning boundaries.',
  }, startX + COUPLE_HORIZONTAL_OFFSET, startY);

  // Child 1 - Daughter
  const child1 = createPerson({
    name: 'Maya Martinez',
    sex: 'female',
    age: 16,
    birthYear: 2008,
    culturalHeritageTags: ['mixed', 'hispanic_latino', 'caucasian'],
    location: 'Phoenix, AZ',
    contactInfo: 'maya.m@email.com',
    maritalStatus: 'single',
    occupation: '11th Grade Student',
    education: 'high_school',
    employmentStatus: 'Student',
    substanceUse: ['experimented_with_marijuana'],
    traumaHistory: ['witnessed_fathers_drinking_and_family_conflict'],
    mentalHealthTreatment: ['Individual therapy (started 6 months ago)'],
    therapyNotes: 'Parentified child. Carries anxiety about family.',
    medications: ['Fluoxetine 20mg daily'],
    emotionalBonds: 'Very close to mother (parentified), conflicted with father',
    communicationStyle: 'Explosive anger, then guilt and withdrawal',
    copingStrategies: ['therapy', 'journaling'],
    supportNetwork: 'Mother (enmeshed), school counselor, best friend',
    notes: 'Parentified child. Carries anxiety about family. Afraid father will drink again.',
  }, startX - HORIZONTAL_SPACING, startY + VERTICAL_SPACING);

  // Child 2 - Son
  const child2 = createPerson({
    name: 'Diego Martinez',
    sex: 'male',
    age: 13,
    birthYear: 2011,
    culturalHeritageTags: ['mixed', 'hispanic_latino', 'caucasian'],
    location: 'Phoenix, AZ',
    contactInfo: 'diego.m.gaming@email.com',
    maritalStatus: 'single',
    occupation: '8th Grade Student',
    education: 'middle_school',
    employmentStatus: 'Student',
    substanceUse: ['none'],
    traumaHistory: ['felt_invisible_during_family_crisis'],
    mentalHealthTreatment: ['Family therapy only (refuses individual)'],
    therapyNotes: 'Refuses individual therapy, only participates in family sessions.',
    medications: ['Adderall XR 20mg daily'],
    emotionalBonds: 'Peripheral to family, seeks attention through misbehavior',
    communicationStyle: 'Acts out, class clown, defiance',
    copingStrategies: ['gaming', 'soccer'],
    supportNetwork: 'Gaming friends online, soccer team',
    notes: 'Feels overshadowed by sister. Acts out for attention. Angry at both parents.',
  }, startX + HORIZONTAL_SPACING, startY + VERTICAL_SPACING);

  commands.push(new CreateNodeCommand(father.node));
  commands.push(new CreateNodeCommand(mother.node));
  commands.push(new CreateNodeCommand(child1.node));
  commands.push(new CreateNodeCommand(child2.node));

  commands.push(createMarriage(father.id, mother.id));
  commands.push(createChild(father.id, child1.id, 0));
  commands.push(createChild(mother.id, child1.id, 1));
  commands.push(createChild(father.id, child2.id, 2));
  commands.push(createChild(mother.id, child2.id, 3));

  return new CompositeCommand('Create Family Therapy Template', commands);
}

/**
 * FAMILY THERAPY – Fusion Map (Tan-Miller cultural acculturation scenario)
 * Focus: 3 generations with acculturation gap + step-parent/step-child relationships
 */
export function createFusionMapTemplate(startX: number, startY: number): CompositeCommand {
  const commands: (CreateNodeCommand | CreateEdgeCommand)[] = [];

  // Generation 1: Singaporean grandparents
  const grandfather = createPerson({
    name: 'Wei Tan',
    sex: 'male',
    age: 74,
    birthYear: 1950,
    culturalHeritageTags: ['asian', 'singaporean', 'chinese'],
    location: 'Singapore (Generation 1)',
    occupation: 'Retired engineer',
    notes: 'Mandarin-speaking; values traditional hierarchy; pushes for cultural rituals every holiday.'
  }, startX - HORIZONTAL_SPACING, startY - VERTICAL_SPACING * 2);

  const grandmother = createPerson({
    name: 'Lian Tan',
    sex: 'female',
    age: 72,
    birthYear: 1952,
    culturalHeritageTags: ['asian', 'singaporean', 'chinese'],
    location: 'Singapore (Generation 1)',
    occupation: 'Retired teacher',
    notes: 'Holds tight to Singaporean traditions; struggles with grandchildren’s American habits.'
  }, startX + HORIZONTAL_SPACING, startY - VERTICAL_SPACING * 2);

  // Generation 2: Adult child + remarriage
  const father = createPerson({
    name: 'Daniel Tan',
    sex: 'male',
    age: 45,
    birthYear: 1979,
    isIndexPerson: true,
    culturalHeritageTags: ['asian', 'singaporean', 'immigrant'],
    location: 'Seattle, WA',
    occupation: 'Product designer',
    notes: 'Bridge between traditions and kids’ US norms; bilingual but defaulting to English at home.'
  }, startX, startY - VERTICAL_SPACING);

  const mother = createPerson({
    name: 'Laura Miller',
    sex: 'female',
    age: 43,
    birthYear: 1981,
    culturalHeritageTags: ['american'],
    location: 'Seattle, WA',
    occupation: 'School counselor',
    notes: 'Co-parents collaboratively; more individualistic parenting style.'
  }, startX - COUPLE_HORIZONTAL_OFFSET * 1.2, startY - VERTICAL_SPACING);

  const stepmother = createPerson({
    name: 'Nicole Miller',
    sex: 'female',
    age: 41,
    birthYear: 1983,
    culturalHeritageTags: ['american'],
    location: 'Seattle, WA',
    occupation: 'Community organizer',
    notes: 'Step-parent trying to hold respectful boundaries while honoring Tan heritage.'
  }, startX + COUPLE_HORIZONTAL_OFFSET * 1.2, startY - VERTICAL_SPACING);

  // Generation 3: Americanized grandchildren
  const daughter = createPerson({
    name: 'Emily Tan-Miller',
    sex: 'female',
    age: 16,
    birthYear: 2008,
    culturalHeritageTags: ['mixed', 'singaporean', 'american'],
    location: 'Seattle, WA',
    education: 'high_school',
    therapyNotes: 'Feels “too American” for grandparents, “too Asian” at school.',
    notes: 'Mostly English-speaking; resists traditional rituals; strong US teen identity.'
  }, startX - HORIZONTAL_SPACING * 0.7, startY + VERTICAL_SPACING);

  const son = createPerson({
    name: 'Logan Tan-Miller',
    sex: 'male',
    age: 12,
    birthYear: 2012,
    culturalHeritageTags: ['mixed', 'singaporean', 'american'],
    location: 'Seattle, WA',
    education: 'middle_school',
    notes: 'Americanized hobbies; curious but inconsistent with heritage language.'
  }, startX + HORIZONTAL_SPACING * 0.7, startY + VERTICAL_SPACING);

  commands.push(
    new CreateNodeCommand(grandfather.node),
    new CreateNodeCommand(grandmother.node),
    new CreateNodeCommand(father.node),
    new CreateNodeCommand(mother.node),
    new CreateNodeCommand(stepmother.node),
    new CreateNodeCommand(daughter.node),
    new CreateNodeCommand(son.node)
  );

  // Grandparent marriage + link to adult child
  commands.push(
    createMarriage(grandfather.id, grandmother.id),
    createChild(grandfather.id, father.id, 0),
    createChild(grandmother.id, father.id, 1)
  );

  // Parents: first marriage divorced; remarried to step-parent
  commands.push(
    new CreateEdgeCommand({
      type: 'marriage',
      category: 'genogram',
      from: father.id,
      to: mother.id,
      attrs: { divorced: true }
    }),
    createMarriage(father.id, stepmother.id)
  );

  // Children + explicit step-child edge
  commands.push(
    createChild(father.id, daughter.id, 2),
    createChild(mother.id, daughter.id, 3),
    new CreateEdgeCommand({
      type: 'child',
      category: 'genogram',
      from: stepmother.id,
      to: daughter.id,
      fromAnchor: 'bottom',
      toAnchor: 'top',
      attrs: { step: true }
    }),
    createChild(father.id, son.id, 4),
    createChild(stepmother.id, son.id, 5)
  );

  // Emotional lines to show acculturation gap (grandparents ↔ grandkids)
  commands.push(
    new CreateEdgeCommand({
      type: 'line',
      category: 'genogram',
      from: grandfather.id,
      to: daughter.id,
      attrs: { dashed: true, dash: [6, 6], color: '#475569' }
    }),
    new CreateEdgeCommand({
      type: 'line',
      category: 'genogram',
      from: grandmother.id,
      to: son.id,
      attrs: { dashed: true, dash: [6, 6], color: '#475569' }
    })
  );

  return new CompositeCommand('Create Fusion Map Template', commands);
}

/**
 * FAMILY THERAPY – Bunker Family Conflict Map (Mark, Sarah, Leo, Maya)
 * Only used for the family-therapy Conflict Map tile.
 */
export function createConflictMapTemplate(startX: number, startY: number): CompositeCommand {
  const commands: (CreateNodeCommand | CreateEdgeCommand)[] = [];

  const mark = createPerson({
    name: 'Mark Bunker',
    sex: 'male',
    age: 40,
    occupation: 'Contractor',
    maritalStatus: 'divorced',
    communicationStyle: 'Escalates during conflict; blames partner',
    copingStrategies: ['individual_therapy'],
    notes: 'Divorced and hostile co-parenting with Sarah.'
  }, startX - COUPLE_HORIZONTAL_OFFSET, startY);

  const sarah = createPerson({
    name: 'Sarah Bunker',
    sex: 'female',
    age: 38,
    isIndexPerson: true,
    occupation: 'Nurse',
    maritalStatus: 'divorced',
    therapyNotes: 'Working on boundaries and co-parenting plans.',
    copingStrategies: ['therapy', 'journaling'],
    notes: 'Primary custodian; tension with Mark over routines.'
  }, startX + COUPLE_HORIZONTAL_OFFSET, startY);

  const leo = createPerson({
    name: 'Leo Bunker',
    sex: 'male',
    age: 15,
    education: 'high_school',
    communicationStyle: 'Rebel; clashes with dad',
    notes: 'Acts out to distract parents from fighting.'
  }, startX + HORIZONTAL_SPACING * 0.6, startY + VERTICAL_SPACING);

  const maya = createPerson({
    name: 'Maya Bunker',
    sex: 'female',
    age: 10,
    education: 'elementary_school',
    communicationStyle: 'Peacemaker; clings to mom',
    notes: 'Stays “perfect” and close to mom to ease her loneliness.'
  }, startX - HORIZONTAL_SPACING * 0.6, startY + VERTICAL_SPACING);

  commands.push(
    new CreateNodeCommand(mark.node),
    new CreateNodeCommand(sarah.node),
    new CreateNodeCommand(leo.node),
    new CreateNodeCommand(maya.node)
  );

  // Parents: divorced + hostile conflict line
  commands.push(
    new CreateEdgeCommand({
      type: 'marriage',
      category: 'genogram',
      from: mark.id,
      to: sarah.id,
      attrs: { divorced: true }
    }),
    new CreateEdgeCommand({
      type: 'conflict',
      category: 'genogram',
      from: mark.id,
      to: sarah.id,
      attrs: { doubleLine: true }
    })
  );

  // Children attachments
  commands.push(
    createChild(mark.id, leo.id, 0),
    createChild(sarah.id, leo.id, 1),
    createChild(mark.id, maya.id, 2),
    createChild(sarah.id, maya.id, 3)
  );

  // Emotional lines per scenario
  commands.push(
    new CreateEdgeCommand({
      type: 'conflict',
      category: 'genogram',
      from: mark.id,
      to: leo.id,
      attrs: {}
    }),
    new CreateEdgeCommand({
      type: 'line',
      category: 'genogram',
      from: sarah.id,
      to: leo.id,
      attrs: { dashed: true, dash: [6, 6], color: '#94A3B8' }
    }),
    new CreateEdgeCommand({
      type: 'fused',
      category: 'genogram',
      from: sarah.id,
      to: maya.id
    }),
    new CreateEdgeCommand({
      type: 'line',
      category: 'genogram',
      from: mark.id,
      to: maya.id,
      attrs: { color: '#475569', width: 3, dashed: false }
    })
  );

  return new CompositeCommand('Create Conflict Map Template', commands);
}

/**
 * FAMILY THERAPY – Legacy Map (Miller family, 3 generations)
 * Only used for the family-therapy Legacy Map tile.
 */
export function createLegacyMapTemplate(startX: number, startY: number): CompositeCommand {
  const commands: (CreateNodeCommand | CreateEdgeCommand)[] = [];

  // Generation 1: Arthur & Martha (grandparents)
  const arthur = createPerson({
    name: 'Arthur Miller',
    sex: 'male',
    age: 76,
    birthYear: 1949,
    medicalConditionTags: ['stroke', 'type_2_diabetes'],
    substanceUse: ['smoking_history'],
    riskFactors: ['high_salt_diet'],
    notes: 'Minor stroke at 68; Type 2 diabetes since mid-50s. Former pack-a-day smoker; diet heavy in salt and saturated fat.'
  }, startX - COUPLE_HORIZONTAL_OFFSET, startY - VERTICAL_SPACING * 2);

  const martha = createPerson({
    name: 'Martha Miller',
    sex: 'female',
    age: 74,
    birthYear: 1951,
    medicalConditionTags: ['osteoporosis'],
    riskFactors: ['second_hand_smoke'],
    notes: 'Raised family in a smoking household; diet centered on meat and butter. Active with gardening and walking to shops.'
  }, startX + COUPLE_HORIZONTAL_OFFSET, startY - VERTICAL_SPACING * 2);

  // Generation 2: David and Sarah (parents)
  const david = createPerson({
    name: 'David Miller',
    sex: 'male',
    age: 44,
    birthYear: 1980,
    medicalConditionTags: ['hypertension', 'high_cholesterol'],
    occupation: 'Operations Manager',
    employmentStatus: 'Full-time',
    maritalStatus: 'married',
    riskFactors: ['sedentary_work', 'high_stress', 'processed_food_diet'],
    notes: 'Diagnosed with hypertension and high cholesterol at 42. Sedentary desk work; high takeout and caffeine; poor sleep (5–6 hrs).'
  }, startX - COUPLE_HORIZONTAL_OFFSET, startY - VERTICAL_SPACING);

  const sarah = createPerson({
    name: 'Sarah Miller',
    sex: 'female',
    age: 43,
    birthYear: 1981,
    occupation: 'Project Coordinator',
    maritalStatus: 'married',
    medicalConditionTags: ['joint_pain'],
    notes: 'Generally healthy; intermittent joint pain. Sedentary office work; inconsistent gym attendance.'
  }, startX + COUPLE_HORIZONTAL_OFFSET, startY - VERTICAL_SPACING);

  // Generation 3: Leo (adult child)
  const leo = createPerson({
    name: 'Leo Miller',
    sex: 'male',
    age: 28,
    birthYear: 1996,
    isIndexPerson: true,
    medicalConditionTags: ['borderline_high_ldl'],
    copingStrategies: ['strength_training', 'wearables'],
    riskFactors: ['family_history_cholesterol'],
    notes: 'Borderline high LDL at 28 despite clean, high-protein diet and 4x/week strength training. Tracks biometrics; social drinking noted.'
  }, startX, startY + VERTICAL_SPACING);

  commands.push(
    new CreateNodeCommand(arthur.node),
    new CreateNodeCommand(martha.node),
    new CreateNodeCommand(david.node),
    new CreateNodeCommand(sarah.node),
    new CreateNodeCommand(leo.node)
  );

  commands.push(
    createMarriage(arthur.id, martha.id),
    createMarriage(david.id, sarah.id),
    createChild(arthur.id, david.id, 0),
    createChild(martha.id, david.id, 1),
    createChild(david.id, leo.id, 2),
    createChild(sarah.id, leo.id, 3)
  );

  return new CompositeCommand('Create Legacy Map Template', commands);
}

/**
 * SOCIAL WORK USE CASE - Focus on housing, employment, social services
 */
export function createSocialWorkTemplate(startX: number, startY: number): CompositeCommand {
  const commands: (CreateNodeCommand | CreateEdgeCommand)[] = [];

  // Mother (Index Person)
  const mother = createPerson({
    name: 'Shanice Williams',
    sex: 'female',
    age: 34,
    birthYear: 1990,
    isIndexPerson: true,
    culturalHeritageTags: ['african_american'],
    location: 'Detroit, MI',
    contactInfo: 'Shelter: New Hope Center',
    maritalStatus: 'separated',
    housingStatus: 'Homeless - currently in family shelter',
    occupation: 'Retail Cashier (laid off)',
    educationLevel: 'high_school',
    employmentStatus: 'Unemployed',
    income: 'Below poverty line - receiving SNAP, TANF',
    assets: 'None',
    debts: '$8,500 credit card debt, $15,000 medical debt',
    insurance: 'Medicaid',
    servicesReceived: ['SNAP', 'TANF', 'shelter_services', 'job_training'],
    socialNotes: 'Enrolled in job training program. Waiting for Section 8 housing voucher.',
    strengths: ['resilient', 'protective_of_children', 'seeking_help'],
    medicalConditionTags: ['asthma'],
    substanceUse: ['none_currently'],
    traumaHistory: ['domestic_violence_survivor'],
    mentalHealthTreatment: ['Crisis counseling at shelter'],
    therapyNotes: 'Left abusive partner 6 months ago. Working with case manager.',
    medications: ['Sertraline 50mg (when available)', 'Albuterol inhaler'],
    legalStatus: 'Restraining order against ex-partner',
    legalNotes: 'Full custody of children',
    supportNetwork: 'Case manager at shelter, sister (limited support)',
    notes: 'Enrolled in job training program. Waiting for Section 8 housing voucher.',
  }, startX, startY);

  // Child 1
  const child1 = createPerson({
    name: 'Jayden Williams',
    sex: 'male',
    age: 8,
    birthYear: 2016,
    culturalHeritageTags: ['african_american'],
    location: 'Detroit, MI - New Hope Shelter',
    contactInfo: 'Via mother',
    maritalStatus: 'single',
    housingStatus: 'Homeless - in family shelter',
    occupation: '3rd Grade Student',
    educationLevel: 'elementary',
    employmentStatus: 'Student',
    income: 'Dependent',
    servicesReceived: ['free_lunch', 'school_counseling', 'shelter_play_therapy'],
    socialNotes: 'Behavioral issues at school. Struggling with frequent moves.',
    medicalConditionTags: [],
    substanceUse: [],
    traumaHistory: ['witnessed_domestic_violence', 'recent_homelessness'],
    mentalHealthTreatment: ['School counselor', 'Shelter play therapy'],
    medications: [],
    supportNetwork: 'Mother, school counselor, shelter staff',
    notes: 'Behavioral issues at school. Struggling with frequent moves. Receives free lunch.',
  }, startX - COUPLE_HORIZONTAL_OFFSET, startY + VERTICAL_SPACING);

  // Child 2
  const child2 = createPerson({
    name: 'Aaliyah Williams',
    sex: 'female',
    age: 5,
    birthYear: 2019,
    culturalHeritageTags: ['african_american'],
    location: 'Detroit, MI - New Hope Shelter',
    contactInfo: 'Via mother',
    maritalStatus: 'single',
    housingStatus: 'Homeless - in family shelter',
    occupation: 'Kindergarten',
    educationLevel: 'elementary',
    employmentStatus: 'Student',
    income: 'Dependent',
    insurance: 'Medicaid',
    servicesReceived: ['medicaid', 'free_lunch'],
    medicalConditionTags: ['asthma'],
    traumaHistory: ['witnessed_domestic_violence'],
    mentalHealthTreatment: [],
    medications: ['Albuterol inhaler'],
    supportNetwork: 'Mother, kindergarten teacher',
    notes: 'Clingy with mother. Nightmares. Receives Medicaid, free lunch.',
  }, startX + COUPLE_HORIZONTAL_OFFSET, startY + VERTICAL_SPACING);

  // Grandmother
  const grandmother = createPerson({
    name: 'Dorothy Williams',
    sex: 'female',
    age: 62,
    birthYear: 1962,
    culturalHeritageTags: ['african_american'],
    location: 'Atlanta, GA',
    contactInfo: '(404) 555-0199',
    maritalStatus: 'widowed',
    housingStatus: 'Owns home',
    occupation: 'Retired School Cafeteria Worker',
    educationLevel: 'high_school',
    employmentStatus: 'Retired',
    income: 'Fixed income - Social Security, small pension',
    assets: 'Home (paid off)',
    debts: 'None',
    insurance: 'Medicare',
    medicalConditionTags: ['diabetes', 'hypertension'],
    medications: ['Metformin', 'Lisinopril'],
    supportNetwork: 'Church community',
    notes: 'Wants to help daughter but limited resources. Health prevents taking in grandchildren.',
  }, startX, startY - VERTICAL_SPACING);

  commands.push(new CreateNodeCommand(mother.node));
  commands.push(new CreateNodeCommand(child1.node));
  commands.push(new CreateNodeCommand(child2.node));
  commands.push(new CreateNodeCommand(grandmother.node));

  commands.push(createChild(grandmother.id, mother.id, 0));
  commands.push(createChild(mother.id, child1.id, 1));
  commands.push(createChild(mother.id, child2.id, 2));

  return new CompositeCommand('Create Social Work Template', commands);
}

/**
 * LEGAL/ESTATE USE CASE - Focus on custody, guardianship, wills, estate planning
 */
export function createLegalEstateTemplate(startX: number, startY: number): CompositeCommand {
  const commands: (CreateNodeCommand | CreateEdgeCommand)[] = [];

  // Grandfather (deceased, estate issues)
  const grandfather = createPerson({
    name: 'William Thompson Sr.',
    sex: 'male',
    age: 88,
    birthYear: 1936,
    deathYear: 2023,
    deceased: true,
    culturalHeritageTags: ['caucasian', 'british'],
    location: 'Charleston, SC',
    maritalStatus: 'widowed',
    occupation: 'Retired Business Owner',
    education: 'bachelors',
    legalStatus: 'Deceased',
    willStatus: 'Contested - probate ongoing',
    legalNotes: 'Will executed 2015, trust established 2010. Estate in probate.',
    executor: 'William Thompson Jr. (son)',
    trustee: 'Trust established 2010',
    assets: 'Real estate holdings $2.5M, business interests $1.8M, investments $900K',
    debts: '$150K business loans',
    causeOfDeath: 'Natural causes - age-related decline',
    notes: 'Estate in probate. Will contested by second wife vs. children from first marriage.',
  }, startX - HORIZONTAL_SPACING, startY - VERTICAL_SPACING * 2);

  // Grandmother (deceased)
  const grandmother = createPerson({
    name: 'Catherine Thompson',
    sex: 'female',
    age: 84,
    birthYear: 1940,
    deathYear: 2018,
    deceased: true,
    culturalHeritageTags: ['caucasian', 'british'],
    location: 'Charleston, SC',
    maritalStatus: 'married',
    occupation: 'Homemaker',
    education: 'high_school',
    legalStatus: 'Deceased',
    willStatus: 'Executed and settled',
    legalNotes: 'Left everything to husband',
    beneficiaries: 'Husband (William Sr.)',
    assets: 'Joint assets with husband',
    causeOfDeath: 'Cancer',
    notes: 'Predeceased husband. Estate passed to William Sr.',
  }, startX + HORIZONTAL_SPACING, startY - VERTICAL_SPACING * 2);

  // Father (Index Person - executor)
  const father = createPerson({
    name: 'William Thompson Jr.',
    sex: 'male',
    age: 58,
    birthYear: 1966,
    isIndexPerson: true,
    culturalHeritageTags: ['caucasian', 'british'],
    location: 'Charleston, SC',
    contactInfo: 'wthompson@thompsonlaw.com',
    maritalStatus: 'married',
    occupation: 'Estate Planning Attorney',
    education: 'doctorate',
    employmentStatus: 'Self-employed',
    income: '$250K+ annually',
    legalStatus: 'Executor of fathers estate (contested)',
    guardianship: 'Named guardian for minor nephew',
    willStatus: 'Current will executed 2023',
    legalNotes: 'Managing fathers contested estate. Represents self. Conflict with stepmother over assets.',
    executor: 'Self (for fathers estate)',
    trustee: 'Trustee of family trust',
    powerOfAttorney: 'Spouse has medical and financial POA',
    advanceDirectives: 'Living will on file',
    beneficiaries: 'Spouse and children',
    assets: 'Home $650K, retirement accounts $1.2M, law practice equity $400K',
    debts: '$120K mortgage',
    notes: 'Managing fathers contested estate. Represents self. Conflict with stepmother over assets.',
  }, startX - COUPLE_HORIZONTAL_OFFSET, startY - VERTICAL_SPACING);

  // Mother
  const mother = createPerson({
    name: 'Patricia Thompson',
    sex: 'female',
    age: 56,
    birthYear: 1968,
    culturalHeritageTags: ['caucasian', 'american'],
    location: 'Charleston, SC',
    contactInfo: 'patricia.thompson@email.com',
    maritalStatus: 'married',
    occupation: 'Pediatrician',
    education: 'doctorate',
    employmentStatus: 'Full-time',
    income: '$200K+ annually',
    legalStatus: 'Good standing',
    willStatus: 'Current will executed 2023',
    legalNotes: 'Comprehensive estate plan. All legal documents current and reviewed annually.',
    powerOfAttorney: 'Spouse has medical and financial POA',
    advanceDirectives: 'Living will on file',
    beneficiaries: 'Spouse and children',
    trustee: 'Co-trustee with spouse',
    assets: 'Joint assets with husband, medical practice partnership $300K',
    debts: 'Joint mortgage',
    notes: 'Comprehensive estate plan. All legal documents current and reviewed annually.',
  }, startX + COUPLE_HORIZONTAL_OFFSET, startY - VERTICAL_SPACING);

  // Child with special needs
  const child1 = createPerson({
    name: 'Emma Thompson',
    sex: 'female',
    age: 22,
    birthYear: 2002,
    culturalHeritageTags: ['caucasian'],
    location: 'Charleston, SC',
    contactInfo: 'Via parents',
    maritalStatus: 'single',
    occupation: 'In supported employment program',
    education: 'high_school',
    employmentStatus: 'Part-time (supported)',
    income: 'SSI benefits + family support',
    insurance: 'Medicaid',
    legalStatus: 'Adult ward - parents have guardianship',
    guardianship: 'Parents are legal guardians (court-appointed)',
    willStatus: 'Beneficiary of special needs trust',
    legalNotes: 'Full guardianship by parents. Special needs trust ensures Medicaid eligibility maintained.',
    beneficiaries: 'N/A',
    trustee: 'Parents are trustees of special needs trust',
    assets: 'Special needs trust $500K',
    debts: 'None',
    notes: 'Full guardianship by parents. Special needs trust ensures Medicaid eligibility maintained.',
  }, startX - HORIZONTAL_SPACING, startY);

  // Child (typical)
  const child2 = createPerson({
    name: 'James Thompson',
    sex: 'male',
    age: 19,
    birthYear: 2005,
    culturalHeritageTags: ['caucasian'],
    location: 'Boston, MA',
    contactInfo: 'james.thompson@college.edu',
    maritalStatus: 'single',
    occupation: 'College Student - Pre-Law',
    education: 'some_college',
    employmentStatus: 'Student',
    income: 'Dependent - trust fund for education',
    legalStatus: 'Good standing',
    willStatus: 'Beneficiary of parents estate and grandfathers estate (if settled)',
    legalNotes: 'Named as backup guardian for sister if parents unable. Education fully funded.',
    beneficiaries: 'N/A',
    guardianship: 'Backup guardian for sister Emma',
    assets: 'Education trust $200K',
    debts: 'None (parents cover education)',
    notes: 'Named as backup guardian for sister if parents unable. Education fully funded.',
  }, startX + HORIZONTAL_SPACING, startY);

  commands.push(new CreateNodeCommand(grandfather.node));
  commands.push(new CreateNodeCommand(grandmother.node));
  commands.push(new CreateNodeCommand(father.node));
  commands.push(new CreateNodeCommand(mother.node));
  commands.push(new CreateNodeCommand(child1.node));
  commands.push(new CreateNodeCommand(child2.node));

  commands.push(createMarriage(grandfather.id, grandmother.id));
  commands.push(createMarriage(father.id, mother.id));
  commands.push(createChild(grandfather.id, father.id, 0));
  commands.push(createChild(grandmother.id, father.id, 1));
  commands.push(createChild(father.id, child1.id, 2));
  commands.push(createChild(mother.id, child1.id, 3));
  commands.push(createChild(father.id, child2.id, 4));
  commands.push(createChild(mother.id, child2.id, 5));

  return new CompositeCommand('Create Legal/Estate Template', commands);
}

/**
 * GENERAL RESEARCH USE CASE - Focus on research IDs, study participation, biospecimens
 */
export function createGeneralResearchTemplate(startX: number, startY: number): CompositeCommand {
  const commands: (CreateNodeCommand | CreateEdgeCommand)[] = [];

  // Grandfather
  const grandfather = createPerson({
    name: 'Robert Chen',
    sex: 'male',
    age: 76,
    birthYear: 1948,
    culturalHeritageTags: ['asian', 'chinese'],
    location: 'San Francisco, CA',
    researchId: 'LG-2024-001',
    cohort: 'Control group',
    consentStatus: 'Consented 2022-01-15',
    biospecimen: 'Blood (DNA extracted), saliva, stored',
    collectionDate: '2022-01-20',
    dataQuality: 'High quality',
    dataSources: ['clinical_records', 'biospecimens', 'surveys'],
    researchNotes: 'Excellent compliance with study protocol.',
    medicalConditionTags: ['hypertension', 'type_2_diabetes'],
    geneticMarkers: [],
    intergenerationalPatterns: 'Diabetes common in family',
    notes: 'Longitudinal aging study participant. Annual assessments. Biobank contributor.',
  }, startX - HORIZONTAL_SPACING, startY - VERTICAL_SPACING * 2);

  // Grandmother
  const grandmother = createPerson({
    name: 'Mei Chen',
    sex: 'female',
    age: 74,
    birthYear: 1950,
    culturalHeritageTags: ['asian', 'chinese'],
    location: 'San Francisco, CA',
    researchId: 'LG-2024-002',
    cohort: 'Control group',
    consentStatus: 'Consented 2022-01-15',
    biospecimen: 'Blood (DNA extracted), saliva, stored',
    collectionDate: '2022-01-20',
    dataQuality: 'High quality',
    dataSources: ['clinical_records', 'biospecimens', 'surveys'],
    researchNotes: 'Excellent compliance. Highly motivated participant.',
    medicalConditionTags: ['osteoporosis'],
    geneticMarkers: [],
    intergenerationalPatterns: 'Longevity in maternal line',
    notes: 'Longitudinal aging study participant. Annual assessments. Excellent compliance.',
  }, startX + HORIZONTAL_SPACING, startY - VERTICAL_SPACING * 2);

  // Father (Index Person)
  const father = createPerson({
    name: 'David Chen',
    sex: 'male',
    age: 48,
    birthYear: 1976,
    isIndexPerson: true,
    culturalHeritageTags: ['asian', 'chinese'],
    location: 'Palo Alto, CA',
    contactInfo: 'david.chen@research.stanford.edu',
    researchId: 'GEN-2023-456',
    cohort: 'Case - Early onset diabetes',
    consentStatus: 'Consented 2023-06-01',
    biospecimen: 'Blood, saliva, skin biopsy, gut microbiome, stored at -80°C',
    collectionDate: '2023-06-15',
    dataQuality: 'High quality - complete dataset',
    dataSources: ['clinical_records', 'biospecimens', 'CGM_data', 'surveys'],
    researchNotes: 'Enrolled in precision medicine trial. Quarterly assessments. CGM monitoring.',
    publicationStatus: 'Data used in 2 publications',
    followUpStatus: 'Active - quarterly visits',
    medicalConditionTags: ['type_2_diabetes', 'pre_diabetes_at_age_32'],
    geneticMarkers: ['Polygenic risk score: high for T2D'],
    medications: ['Metformin 1000mg BID', 'Empagliflozin 10mg daily'],
    intergenerationalPatterns: 'Father has T2D, grandfather has T2D',
    occupation: 'Research Scientist',
    education: 'doctorate',
    notes: 'Enrolled in precision medicine trial. Quarterly assessments. CGM monitoring.',
  }, startX - COUPLE_HORIZONTAL_OFFSET, startY - VERTICAL_SPACING);

  // Mother
  const mother = createPerson({
    name: 'Lisa Park',
    sex: 'female',
    age: 46,
    birthYear: 1978,
    culturalHeritageTags: ['asian', 'korean'],
    location: 'Palo Alto, CA',
    contactInfo: 'lisa.chen@email.com',
    researchId: 'GEN-2023-457',
    cohort: 'Control - No diabetes',
    consentStatus: 'Consented 2023-06-01',
    biospecimen: 'Blood, saliva, gut microbiome',
    collectionDate: '2023-06-15',
    dataQuality: 'High quality',
    dataSources: ['clinical_records', 'biospecimens', 'surveys'],
    researchNotes: 'Control participant in spouses study.',
    followUpStatus: 'Active - quarterly visits',
    medicalConditionTags: [],
    geneticMarkers: ['Polygenic risk score: low for T2D'],
    intergenerationalPatterns: 'No diabetes in family history',
    occupation: 'Data Analyst',
    education: 'masters',
    notes: 'Control participant in spouses study. Quarterly assessments.',
  }, startX + COUPLE_HORIZONTAL_OFFSET, startY - VERTICAL_SPACING);

  // Child 1
  const child1 = createPerson({
    name: 'Emily Chen',
    sex: 'female',
    age: 16,
    birthYear: 2008,
    culturalHeritageTags: ['mixed', 'chinese', 'korean'],
    location: 'Palo Alto, CA',
    researchId: 'PEDS-2023-089',
    cohort: 'High risk offspring - intervention arm',
    consentStatus: 'Parental consent + youth assent 2023-06-15',
    biospecimen: 'Blood, saliva (DNA), continuous glucose monitoring data',
    collectionDate: '2023-06-20',
    dataQuality: 'High quality',
    dataSources: ['clinical_records', 'biospecimens', 'CGM_data', 'lifestyle_surveys'],
    researchNotes: 'Lifestyle intervention arm. Good adherence to protocol.',
    followUpStatus: 'Active - monthly visits',
    medicalConditionTags: ['pre_diabetes'],
    geneticMarkers: ['Polygenic risk score: high for T2D (inherited from father)'],
    intergenerationalPatterns: 'Paternal diabetes history',
    occupation: 'High School Student',
    education: 'high_school',
    notes: 'Pediatric prevention study. CGM monitoring. Lifestyle intervention arm.',
  }, startX - HORIZONTAL_SPACING, startY);

  // Child 2
  const child2 = createPerson({
    name: 'Ryan Chen',
    sex: 'male',
    age: 12,
    birthYear: 2012,
    culturalHeritageTags: ['mixed', 'chinese', 'korean'],
    location: 'Palo Alto, CA',
    researchId: 'PEDS-2023-090',
    cohort: 'High risk offspring - standard care arm',
    consentStatus: 'Parental consent + youth assent 2023-06-15',
    biospecimen: 'Blood, saliva (DNA), CGM data',
    collectionDate: '2023-06-20',
    dataQuality: 'High quality',
    dataSources: ['clinical_records', 'biospecimens', 'CGM_data'],
    researchNotes: 'Standard care comparison group.',
    followUpStatus: 'Active - quarterly visits',
    medicalConditionTags: [],
    geneticMarkers: ['Polygenic risk score: moderate for T2D'],
    intergenerationalPatterns: 'Paternal diabetes history',
    occupation: '7th Grade Student',
    education: 'middle_school',
    notes: 'Pediatric prevention study. CGM monitoring. Standard care arm for comparison.',
  }, startX + HORIZONTAL_SPACING, startY);

  commands.push(new CreateNodeCommand(grandfather.node));
  commands.push(new CreateNodeCommand(grandmother.node));
  commands.push(new CreateNodeCommand(father.node));
  commands.push(new CreateNodeCommand(mother.node));
  commands.push(new CreateNodeCommand(child1.node));
  commands.push(new CreateNodeCommand(child2.node));

  commands.push(createMarriage(grandfather.id, grandmother.id));
  commands.push(createMarriage(father.id, mother.id));
  commands.push(createChild(grandfather.id, father.id, 0));
  commands.push(createChild(grandmother.id, father.id, 1));
  commands.push(createChild(father.id, child1.id, 2));
  commands.push(createChild(mother.id, child1.id, 3));
  commands.push(createChild(father.id, child2.id, 4));
  commands.push(createChild(mother.id, child2.id, 5));

  return new CompositeCommand('Create General Research Template', commands);
}

/**
 * GENERAL RESEARCH – Genetic History Map (medical lineage patterns)
 */
export function createGeneticHistoryMapTemplate(startX: number, startY: number): CompositeCommand {
  const commands: (CreateNodeCommand | CreateEdgeCommand)[] = [];

  // Level 1: Paternal grandparents
  const paternalGrandfather = createPerson({
    name: 'George Miller',
    sex: 'male',
    age: 78,
    birthYear: 1946,
    deathYear: 2020,
    deceased: true,
    medicalConditionTags: ['heart_disease'],
    causeOfDeath: 'Heart failure',
    intergenerationalPatterns: 'Cardiac risk on paternal line',
    notes: 'Died of heart failure in late 70s.'
  }, startX - COUPLE_HORIZONTAL_OFFSET, startY - VERTICAL_SPACING * 2);

  const paternalGrandmother = createPerson({
    name: 'Margaret Miller',
    sex: 'female',
    age: 90,
    birthYear: 1934,
    notes: 'Lived to 90; no major cardiac history.'
  }, startX + COUPLE_HORIZONTAL_OFFSET, startY - VERTICAL_SPACING * 2);

  // Level 2: Parents
  const father = createPerson({
    name: 'Michael Miller',
    sex: 'male',
    age: 65,
    birthYear: 1959,
    deathYear: 2024,
    deceased: true,
    medicalConditionTags: ['heart_disease'],
    causeOfDeath: 'Heart attack @ 65',
    notes: 'Fatal heart attack at 65.'
  }, startX - COUPLE_HORIZONTAL_OFFSET, startY - VERTICAL_SPACING);

  const mother = createPerson({
    name: 'Susan Miller',
    sex: 'female',
    age: 63,
    birthYear: 1961,
    notes: 'No known cardiac conditions.'
  }, startX + COUPLE_HORIZONTAL_OFFSET, startY - VERTICAL_SPACING);

  // Level 3: Subject + siblings
  const subject = createPerson({
    name: 'Daniel Miller',
    sex: 'male',
    age: 40,
    birthYear: 1984,
    isIndexPerson: true,
    medicalConditionTags: [],
    notes: 'Proband; tracking cardiac risk.'
  }, startX, startY);

  const brother = createPerson({
    name: 'James Miller',
    sex: 'male',
    age: 37,
    birthYear: 1987,
    medicalConditionTags: ['hypertension'],
    notes: 'High blood pressure; monitoring.'
  }, startX - HORIZONTAL_SPACING * 0.7, startY);

  const sister = createPerson({
    name: 'Emily Miller',
    sex: 'female',
    age: 34,
    birthYear: 1990,
    notes: 'No known cardiac issues.'
  }, startX + HORIZONTAL_SPACING * 0.7, startY);

  commands.push(
    new CreateNodeCommand(paternalGrandfather.node),
    new CreateNodeCommand(paternalGrandmother.node),
    new CreateNodeCommand(father.node),
    new CreateNodeCommand(mother.node),
    new CreateNodeCommand(subject.node),
    new CreateNodeCommand(brother.node),
    new CreateNodeCommand(sister.node)
  );

  commands.push(
    createMarriage(paternalGrandfather.id, paternalGrandmother.id),
    createMarriage(father.id, mother.id),
    createChild(paternalGrandfather.id, father.id, 0),
    createChild(paternalGrandmother.id, father.id, 1),
    createChild(father.id, brother.id, 2),
    createChild(mother.id, brother.id, 3),
    createChild(father.id, subject.id, 4),
    createChild(mother.id, subject.id, 5),
    createChild(father.id, sister.id, 6),
    createChild(mother.id, sister.id, 7)
  );

  return new CompositeCommand('Create Genetic History Map Template', commands);
}

/**
 * GENERAL RESEARCH – Behavioral Pattern Map (psychosocial patterns)
 */
export function createBehavioralPatternMapTemplate(startX: number, startY: number): CompositeCommand {
  const commands: (CreateNodeCommand | CreateEdgeCommand)[] = [];

  // Level 1: Maternal grandparents
  const grandmother = createPerson({
    name: 'Elaine Carter',
    sex: 'female',
    age: 78,
    birthYear: 1946,
    occupation: 'Teacher',
    education: 'bachelors',
    notes: 'Former school teacher; long estrangement from daughter.'
  }, startX - COUPLE_HORIZONTAL_OFFSET, startY - VERTICAL_SPACING * 2);

  const grandfather = createPerson({
    name: 'Frank Carter',
    sex: 'male',
    age: 80,
    birthYear: 1944,
    occupation: 'Factory Supervisor',
    notes: 'Maintained a distant family role.'
  }, startX + COUPLE_HORIZONTAL_OFFSET, startY - VERTICAL_SPACING * 2);

  // Level 2: Parents
  const mother = createPerson({
    name: 'Patricia Carter',
    sex: 'female',
    age: 52,
    birthYear: 1972,
    education: 'high_school',
    notes: 'High school dropout; estranged from mother after conflict.'
  }, startX - HORIZONTAL_SPACING * 1.6, startY - VERTICAL_SPACING);

  const father = createPerson({
    name: 'David Clark',
    sex: 'male',
    age: 54,
    birthYear: 1970,
    education: 'high_school',
    notes: 'Left school early; stable employment history.'
  }, startX + COUPLE_HORIZONTAL_OFFSET, startY - VERTICAL_SPACING);

  // Level 3: Subject
  const subject = createPerson({
    name: 'Jordan Clark',
    sex: 'unknown',
    age: 28,
    birthYear: 1996,
    isIndexPerson: true,
    education: 'doctorate',
    occupation: 'PhD Student',
    notes: 'Educational resilience pattern; pursuing doctorate.'
  }, startX, startY);

  commands.push(
    new CreateNodeCommand(grandmother.node),
    new CreateNodeCommand(grandfather.node),
    new CreateNodeCommand(mother.node),
    new CreateNodeCommand(father.node),
    new CreateNodeCommand(subject.node)
  );

  commands.push(
    createMarriage(grandmother.id, grandfather.id),
    createMarriage(mother.id, father.id),
    createChild(grandmother.id, mother.id, 0),
    createChild(grandfather.id, mother.id, 1),
    createChild(mother.id, subject.id, 2),
    createChild(father.id, subject.id, 3)
  );

  // Emotional relationship lines
  commands.push(
    new CreateEdgeCommand({
      type: 'cutoff',
      category: 'genogram',
      from: mother.id,
      to: grandmother.id,
      fromAnchor: 'top',
      toAnchor: 'bottom',
      fromOffset: 0.3,
      toOffset: 0.3
    }),
    new CreateEdgeCommand({
      type: 'fused',
      category: 'genogram',
      from: subject.id,
      to: grandmother.id
    }),
    new CreateEdgeCommand({
      type: 'distant',
      category: 'genogram',
      from: subject.id,
      to: father.id,
      fromAnchor: 'right',
      toAnchor: 'left'
    })
  );

  return new CompositeCommand('Create Behavioral Pattern Map Template', commands);
}

/**
 * GENERAL RESEARCH – Heritage & Roots Map (migration + socio-economic shifts)
 */
export function createHeritageRootsMapTemplate(startX: number, startY: number): CompositeCommand {
  const commands: (CreateNodeCommand | CreateEdgeCommand)[] = [];

  // Level 1: Grandparents
  const grandfather = createPerson({
    name: 'Luigi Romano',
    sex: 'male',
    age: 88,
    birthYear: 1936,
    culturalHeritageTags: ['italian'],
    location: 'Naples, Italy',
    occupation: 'Laborer',
    notes: 'Immigrated to New York in 1920; worked as a dock laborer.'
  }, startX - COUPLE_HORIZONTAL_OFFSET, startY - VERTICAL_SPACING * 2);

  const grandmother = createPerson({
    name: 'Rosa Romano',
    sex: 'female',
    age: 86,
    birthYear: 1938,
    culturalHeritageTags: ['italian'],
    location: 'Naples, Italy',
    occupation: 'Seamstress',
    notes: 'Immigrated to New York in 1920; supported family business.'
  }, startX + COUPLE_HORIZONTAL_OFFSET, startY - VERTICAL_SPACING * 2);

  // Level 2: Parents
  const father = createPerson({
    name: 'Marco Romano',
    sex: 'male',
    age: 60,
    birthYear: 1964,
    culturalHeritageTags: ['italian_american'],
    location: 'Queens, NY',
    occupation: 'Manager',
    education: 'bachelors',
    notes: 'First-generation college graduate; moved to the suburbs.'
  }, startX - COUPLE_HORIZONTAL_OFFSET, startY - VERTICAL_SPACING);

  const mother = createPerson({
    name: 'Giulia Romano',
    sex: 'female',
    age: 58,
    birthYear: 1966,
    culturalHeritageTags: ['italian_american'],
    location: 'Queens, NY',
    occupation: 'Accountant',
    education: 'bachelors',
    notes: 'Entered middle class; focused on family stability.'
  }, startX + COUPLE_HORIZONTAL_OFFSET, startY - VERTICAL_SPACING);

  // Level 3: Subject
  const subject = createPerson({
    name: 'Sofia Romano',
    sex: 'female',
    age: 30,
    birthYear: 1994,
    isIndexPerson: true,
    culturalHeritageTags: ['italian_american'],
    location: 'Milan, Italy',
    occupation: 'Digital Nomad',
    education: 'masters',
    notes: 'Relocated to Italy for work; circular migration pattern.'
  }, startX, startY);

  commands.push(
    new CreateNodeCommand(grandfather.node),
    new CreateNodeCommand(grandmother.node),
    new CreateNodeCommand(father.node),
    new CreateNodeCommand(mother.node),
    new CreateNodeCommand(subject.node)
  );

  commands.push(
    createMarriage(grandfather.id, grandmother.id),
    createMarriage(father.id, mother.id),
    createChild(grandfather.id, father.id, 0),
    createChild(grandmother.id, father.id, 1),
    createChild(father.id, subject.id, 2),
    createChild(mother.id, subject.id, 3)
  );

  // Migration direction lines
  commands.push(
    new CreateEdgeCommand({
      type: 'focused_on',
      category: 'genogram',
      from: grandfather.id,
      to: father.id
    }),
    new CreateEdgeCommand({
      type: 'focused_on',
      category: 'genogram',
      from: father.id,
      to: subject.id
    })
  );

  return new CompositeCommand('Create Heritage & Roots Map Template', commands);
}
